import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { loadConnectAndInitialize } from '@stripe/connect-js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTheme } from '@/hooks/useTheme'
import { getAdminFontSources } from '@/lib/admin-font'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { createStripeConnectAccountSession } from '@/lib/services/stripe-connect/stripe-connect.api'
import { stripeConnectQueryKeys } from '@/lib/services/stripe-connect/stripe-connect.query'
import {
  stripeMigrationPolling,
  stripeMigrationQueries,
} from '@/lib/services/stripe-connect/stripe-migration.query'
import { terminalQueryKeys } from '@/lib/services/terminals/terminals.query'
import { registerActiveStripeConnectInstance } from '@/lib/stripe-connect-session'
import { buildStripeConnectAppearance } from './stripeConnectAppearance'

const StripeConnectOrgSessionContext = createContext(null)
const STRIPE_CONNECT_DEFAULT_SESSION_SURFACE = 'management'

export function sharedStripeConnectSessionSurface(surface) {
  switch (surface) {
    case 'onboarding':
      return 'onboarding'
    case 'payments':
    case 'disputes':
    case 'card-readers':
      return surface
    case 'banner':
    case 'account':
    case 'payouts':
    case 'management':
    case undefined:
    case null:
      // Keep one long-lived management session across the Stripe hub, alerts, and account pages so
      // Stripe-owned authentication does not repeat on every in-app navigation. We intentionally
      // keep onboarding isolated because it has distinct Stripe permissions and lifecycle from
      // the broader post-onboarding management surfaces.
      return STRIPE_CONNECT_DEFAULT_SESSION_SURFACE
    default:
      return STRIPE_CONNECT_DEFAULT_SESSION_SURFACE
  }
}

export function StripeConnectOrgSessionProvider({ organizationId, children }) {
  const queryClient = useQueryClient()
  const { theme } = useTheme()
  const latestThemeRef = useRef(theme)
  const connectInstanceRef = useRef(null)
  const connectInstanceOwnerRef = useRef(null)
  const connectInstanceSessionSurfaceRef = useRef(null)
  const connectInitializationRef = useRef(null)
  const connectInstanceGenerationRef = useRef(0)
  const isMountedRef = useRef(true)
  const [connectInstance, setConnectInstance] = useState(null)
  const [connectAccountVersion, setConnectAccountVersion] = useState(null)
  const [connectInstanceSessionSurface, setConnectInstanceSessionSurface] = useState(null)
  const [resetVersion, setResetVersion] = useState(0)
  // Standalone Stripe routes have no banner poll. A published v1 session proves account-tool
  // authorization; reuse the status query until cutover, even if migration has not started yet.
  const { data: migration } = useQuery({
    ...stripeMigrationQueries.status({ organizationId }),
    enabled: connectAccountVersion === 'v1',
    refetchInterval: stripeMigrationPolling.background,
  })
  const previousActivationRef = useRef(migration?.activated)

  const retireConnectInstance = useCallback(() => {
    // Invalidate every callback owned by the retiring Connect.js instance synchronously. React
    // state updates can lag behind navigation events, but a retired instance must never consume
    // the next instance's secret or report an error into its UI.
    connectInstanceGenerationRef.current += 1
    connectInstanceRef.current = null
    connectInstanceOwnerRef.current = null
    connectInstanceSessionSurfaceRef.current = null
    connectInitializationRef.current = null
    // Keep the last initialized instance registered until its replacement is ready. An auth or
    // organization transition during that gap still needs a best-effort Stripe logout target.
    setConnectInstance(null)
    setConnectAccountVersion(null)
    setConnectInstanceSessionSurface(null)
  }, [])

  const resetConnectInstance = useCallback(() => {
    retireConnectInstance()
    // Wake consumers even when an in-flight initialization has not published an instance yet.
    setResetVersion((version) => version + 1)
  }, [retireConnectInstance])

  const handleActivatedMigration = useCallback(() => {
    const owner = connectInstanceOwnerRef.current || connectInitializationRef.current?.owner
    if (owner?.accountVersion !== 'v2') resetConnectInstance()
    void queryClient.invalidateQueries({
      queryKey: stripeConnectQueryKeys.all({ organizationId }),
    })
    void queryClient.invalidateQueries({
      queryKey: terminalQueryKeys.all({ organizationId }),
    })
  }, [organizationId, queryClient, resetConnectInstance])

  useEffect(() => {
    const wasActivated = previousActivationRef.current
    previousActivationRef.current = migration?.activated
    const owner = connectInstanceOwnerRef.current || connectInitializationRef.current?.owner
    // The first status response can cross cutover too. Use the session's immutable
    // account version, not response ordering, to preserve v2 while retiring retained v1 tools.
    if (!migration?.activated || (wasActivated !== false && owner?.accountVersion !== 'v1')) return
    handleActivatedMigration()
  }, [handleActivatedMigration, migration?.activated])

  useLayoutEffect(() => {
    latestThemeRef.current = theme
  }, [theme])

  const requestAccountSession = useCallback(
    async (surface) => createStripeConnectAccountSession({ organizationId, surface }),
    [organizationId]
  )

  const ensureConnectInstance = useCallback(
    async ({ surface, handleSessionRequestError, subtleBorder = false } = {}) => {
      const requestedSessionSurface = sharedStripeConnectSessionSurface(surface)

      if (
        connectInstanceRef.current &&
        connectInstanceSessionSurfaceRef.current === requestedSessionSurface
      ) {
        connectInstanceOwnerRef.current.handleSessionRequestError =
          handleSessionRequestError ?? null
        connectInstanceOwnerRef.current.subtleBorder = subtleBorder

        connectInstanceRef.current.update({
          appearance: buildStripeConnectAppearance(latestThemeRef.current, { subtleBorder }),
          locale: 'en-US',
        })
        return connectInstanceRef.current
      }

      while (connectInitializationRef.current) {
        const pendingInitialization = connectInitializationRef.current

        if (pendingInitialization.surface === requestedSessionSurface) {
          pendingInitialization.owner.handleSessionRequestError = handleSessionRequestError ?? null
          pendingInitialization.owner.subtleBorder = subtleBorder

          return pendingInitialization.promise
        }

        try {
          await pendingInitialization.promise
        } catch {
          // Let the next initialization attempt request a fresh session for the new surface.
        }

        if (
          connectInstanceRef.current &&
          connectInstanceSessionSurfaceRef.current === requestedSessionSurface
        ) {
          connectInstanceOwnerRef.current.handleSessionRequestError =
            handleSessionRequestError ?? null
          connectInstanceOwnerRef.current.subtleBorder = subtleBorder
          connectInstanceRef.current.update({
            appearance: buildStripeConnectAppearance(latestThemeRef.current, { subtleBorder }),
            locale: 'en-US',
          })

          return connectInstanceRef.current
        }
      }

      retireConnectInstance()
      const generation = connectInstanceGenerationRef.current
      const owner = {
        handleSessionRequestError: handleSessionRequestError ?? null,
        subtleBorder,
        surface: requestedSessionSurface,
      }
      const sourceSessionHasMigrated = () =>
        owner.accountVersion === 'v1' &&
        queryClient.getQueryData(stripeMigrationQueries.status({ organizationId }).queryKey)
          ?.activated

      const initializationPromise = (async () => {
        const session = await requestAccountSession(requestedSessionSurface)

        if (
          !isMountedRef.current ||
          connectInstanceGenerationRef.current !== generation ||
          connectInitializationRef.current?.owner !== owner
        ) {
          return null
        }

        owner.accountVersion = session.account_version
        if (sourceSessionHasMigrated()) {
          // A source response may finish after the activation observer already ran.
          handleActivatedMigration()
          return null
        }

        let initialClientSecret = session.client_secret
        const fetchClientSecret = async () => {
          const ownerIsCurrent = () =>
            isMountedRef.current &&
            connectInstanceGenerationRef.current === generation &&
            (connectInstanceOwnerRef.current === owner ||
              connectInitializationRef.current?.owner === owner)

          if (!ownerIsCurrent()) {
            throw new Error('This Stripe Connect instance is no longer active.')
          }
          if (sourceSessionHasMigrated()) {
            handleActivatedMigration()
            throw new Error('This Stripe Connect instance is no longer active.')
          }

          if (initialClientSecret) {
            const clientSecret = initialClientSecret
            initialClientSecret = null

            return clientSecret
          }

          try {
            const nextSession = await requestAccountSession(owner.surface)

            if (!ownerIsCurrent()) {
              throw new Error('This Stripe Connect instance is no longer active.')
            }
            if (sourceSessionHasMigrated()) {
              handleActivatedMigration()
              throw new Error('This Stripe Connect instance is no longer active.')
            }
            if (nextSession.account_id !== session.account_id) {
              retireConnectInstance()
              throw new Error('The payment account changed. Reopen these Stripe tools.')
            }

            return nextSession.client_secret
          } catch (error) {
            if (ownerIsCurrent()) {
              owner.handleSessionRequestError?.(error)
            }

            throw error
          }
        }

        const nextConnectInstance = loadConnectAndInitialize({
          publishableKey: session.publishable_key,
          fetchClientSecret,
          appearance: buildStripeConnectAppearance(latestThemeRef.current, {
            subtleBorder: owner.subtleBorder,
          }),
          fonts: getAdminFontSources(),
          locale: 'en-US',
        })

        if (
          !isMountedRef.current ||
          connectInstanceGenerationRef.current !== generation ||
          connectInitializationRef.current?.owner !== owner
        ) {
          return null
        }

        connectInstanceRef.current = nextConnectInstance
        connectInstanceOwnerRef.current = owner
        connectInstanceSessionSurfaceRef.current = requestedSessionSurface
        registerActiveStripeConnectInstance({
          organizationId,
          connectInstance: nextConnectInstance,
        })
        setConnectInstance(nextConnectInstance)
        setConnectAccountVersion(session.account_version)
        setConnectInstanceSessionSurface(requestedSessionSurface)

        return nextConnectInstance
      })()

      connectInitializationRef.current = {
        generation,
        owner,
        promise: initializationPromise,
        surface: requestedSessionSurface,
      }

      try {
        return await initializationPromise
      } finally {
        if (connectInitializationRef.current?.generation === generation) {
          connectInitializationRef.current = null
        }
      }
    },
    [
      organizationId,
      queryClient,
      requestAccountSession,
      handleActivatedMigration,
      retireConnectInstance,
    ]
  )

  useEffect(() => {
    if (!connectInstance) {
      return
    }

    connectInstance.update({
      appearance: buildStripeConnectAppearance(theme, {
        subtleBorder: connectInstanceOwnerRef.current?.subtleBorder ?? false,
      }),
      locale: 'en-US',
    })
  }, [connectInstance, theme])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      // A real unmount prevents late publication. Strict Mode can immediately mount this provider
      // again, so instance retirement and logout belong to explicit surface/org/auth transitions.
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      connectInstance,
      connectInstanceSessionSurface,
      ensureConnectInstance,
      resetConnectInstance,
      resetVersion,
    }),
    [
      connectInstance,
      connectInstanceSessionSurface,
      ensureConnectInstance,
      resetConnectInstance,
      resetVersion,
    ]
  )

  return (
    <StripeConnectOrgSessionContext.Provider value={contextValue}>
      {children}
    </StripeConnectOrgSessionContext.Provider>
  )
}

export function useStripeConnectOrgSession() {
  return useContext(StripeConnectOrgSessionContext)
}
